"use client";

import React from "react";
import Link from "next/link";
import type { IBookmarkedFormation } from "@/types/formation";

interface CoachingCardProps {
    title: string;
    /** Page the card opens — both the title and "Découvrir" link to it. */
    href: string;
    category: string;
    /** Short "who it's for" line, shown where TrainingCard shows location/duration. */
    audience: string;
    description: string;
    /** Optional. Coaching is often "sur devis" — omit to hide the price. */
    price?: string;
    /** Optional funding line (e.g. "Financement OPCO"). Hidden when omitted. */
    financing?: string;
    customClass?: string;
    /** When provided, the bookmark becomes an interactive save/remove toggle.
     *  Omit it (e.g. on demo cards) to keep the bookmark inert. */
    formation?: IBookmarkedFormation;
}

const ArrowIcon: React.FC = () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8ZM4.75 7.25C4.33579 7.25 4 7.58579 4 8C4 8.41421 4.33579 8.75 4.75 8.75L9.43934 8.75L8.21967 9.96967C7.92678 10.2626 7.92678 10.7374 8.21967 11.0303C8.51256 11.3232 8.98744 11.3232 9.28033 11.0303L11.7803 8.53033C12.0732 8.23744 12.0732 7.76256 11.7803 7.46967L9.28033 4.96967C8.98744 4.67678 8.51256 4.67678 8.21967 4.96967C7.92678 5.26256 7.92678 5.73744 8.21967 6.03033L9.43934 7.25L4.75 7.25Z"
            fill="#1B365E"
        />
    </svg>
);

/**
 * CoachingCard
 * Same card system as TrainingCard, reduced to a single "Découvrir" action.
 */
export const CoachingCard: React.FC<CoachingCardProps> = ({
    title,
    href,
    category,
    audience,
    description,
    price,
    financing,
    customClass,
}) => {
    const hasPricing = Boolean(price || financing);

    return (
        <div className={`${customClass ?? ""} h-full flex-1 flex flex-col items-center w-full`}>
            <div className="w-full bg-[#C7F8FF] rounded-3xl p-5 min-h-75.75 relative flex flex-col flex-1">
                {/* Header: category tag & bookmark */}
                <div className="flex items-start justify-between mb-2">
                    <span className="bg-white text-[#1B515E] text-[10px] leading-5 font-normal px-2.25 py-1 rounded-2xl">
                        {category}
                    </span>
                    {/* <button
                        type="button"
                        onClick={handleToggleBookmark}
                        disabled={!formation}
                        aria-pressed={formation ? isSaved : undefined}
                        aria-label={isSaved ? "Retirer des favoris" : "Ajouter aux favoris"}
                        className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isSaved ? "bg-[#FC7F16]" : "bg-white"} ${formation ? "cursor-pointer" : "cursor-default"}`}
                    >
                        <BookmarkIcon filled={isSaved} />
                    </button> */}
                </div>

                {/* Content */}
                <div className="flex-1">
                    <h3 className="text-[#1D1D1D] font-bold text-[16px] leading-6.5 mb-0.75">
                        <Link
                            href={href}
                            className="hover:text-[#1B365E] hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1B365E] transition-colors"
                        >
                            {title}
                        </Link>
                    </h3>
                    <p className="text-[#1B365E] font-normal text-[12px] leading-5 mb-3">{audience}</p>
                    <p className="text-[#1D1D1D] font-normal text-[14px] leading-5 mb-5">{description}</p>

                    {hasPricing && (
                        <div className="mb-0.5">
                            {price && (
                                <p className="text-[#1B365E] font-bold text-[15px] leading-4.75 mb-1.25">{price}</p>
                            )}
                            {financing && (
                                <p className="text-[#1B365E] font-normal text-[8px] leading-5 uppercase">{financing}</p>
                            )}
                        </div>
                    )}
                </div>

                <div className="bg-white h-px w-full my-2" />

                {/* Footer: single action */}
                <div className="flex items-center justify-between pt-2 mt-auto">
                    <Link
                        href={href}
                        aria-label={`Découvrir ${title}`}
                        className="flex items-center gap-2 text-[#1B365E] font-bold text-[15px] leading-4.5 cursor-pointer hover:opacity-80 transition-opacity"
                    >
                        <ArrowIcon />
                        Découvrir
                    </Link>
                </div>
            </div>
        </div>
    );
};